/**
 * One invoice — the lines, the tax, and the two irreversible buttons. Redrawn
 * as a record in Wave 15D (C14 §10.6 item 3, ADR-0033; T-6.16): one header
 * with the status, the facts a reader needs first (client, dates, total,
 * balance), **one** primary action and the rest under *More*; the lines, the
 * total and the credit notes as section cards.
 *
 * **The primary action is the next thing the invoice needs**: *Issue* on a
 * draft, *Take payment online* on an issued invoice still owed when the firm's
 * gateway is active, and the PDF otherwise. Everything else — the PDF when it
 * is not the primary, *Credit note*, *Discard draft* — is under *More*, and the
 * two that destroy something are marked as such.
 *
 * **Every rule on this screen is the server's answer, not this file's.**
 * `can_issue` and `can_credit` come down on the payload, computed with the rule
 * the route enforces, so a control greys out for exactly the reason the API
 * would refuse. `issueBlock` adds only the *sentence* — which the server's
 * boolean cannot carry and which is the whole content of the refusal to the
 * person reading it.
 *
 * **The step-up assertion is used once and never kept.** `StepUpDialog` hands
 * it to the callback, the callback puts it on one request, and it goes out of
 * scope. Storing it would be a re-verification that re-verified nothing.
 *
 * **Discarding a draft asks first** (ADR-0033 §1: anything that deletes asks).
 * It was one click before Wave 15; a draft holds no number, so nothing else
 * about it changed.
 *
 * **The PDF opens in a tab rather than downloading.** It is rendered on demand
 * from the invoice's own snapshots (ADR-0011) and nothing is stored, so there is
 * no file to keep — a firm that wants one prints from the tab.
 *
 * **The pay-online control is absent rather than disabled** when the firm's
 * gateway is not active — FR-G7. When it is active (Wave 11), *Take payment
 * online* opens Razorpay Checkout in this page from the session's descriptor
 * (`lib/checkout.ts`, ADR-0027 §2), and a success writes nothing here.
 * `gateway_active` is the server's own read, taken through the same function
 * Wave 5D takes for the portal's button, so the two surfaces cannot disagree
 * about whether a firm can collect online. A disabled button would be a
 * promise; an absent one is the truth, and the sentence under the total says
 * what would make it appear.
 *
 * **C27 (ADR-0056; UAT reports #5, #9, #12, #16):**
 *
 * * **Issue and credit try first** (`useStepUp`, the publish pattern): the
 *   action is sent, and the code is asked for only when the service answers
 *   `STEPUP_REQUIRED`, then the action is sent once more with the assertion.
 *   The reason for a credit note is still asked before anything is sent.
 * * **A draft's tax is editable until it is issued**: the place of supply is
 *   chosen from the state list and *Tax payable on reverse charge* is a
 *   switch, each saved through `billing.updateInvoice` when changed, and the
 *   split the screen shows is the server's answer after it.
 * * **A reverse-charge invoice says so**: its GST is drawn as payable by the
 *   client under reverse charge, and the total is the server's `grand_total`,
 *   which already leaves the tax out (ADR-0056 §4) — nothing is subtracted
 *   here.
 * * **The firm's state unknown is a refusal with a way out**, not a guess:
 *   *Set your firm's state in Settings → Your firm*, with the link.
 */

import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  type DropdownMenuItem,
  Select,
  Switch,
  Textarea,
} from '@fiveox/ui'
import { CreditCard, Download, FileMinus, ListOrdered, Receipt, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { StepUpDialog, useStepUp } from '../../components/StepUpDialog'
import { RecordPage, type RecordFact, RecordSection, useIsPhone } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  type InvoiceDetail as Detail,
  FIRM_STATE_HREF,
  type PaymentSession,
  billing,
  gatewayReason,
  gatewayRefusal,
  isFirmStateUnknown,
  issueBlock,
  iso,
  pdfUrl,
  reverseChargeNote,
  rupees,
  taxRows,
} from '../../lib/api/billing'
import { clients } from '../../lib/api/clients'
import { OWN_STATE, placeOfSupplyOptions } from '../Firm/settings/rules'
import { PAID_NOTICE, openCheckout } from '../../lib/checkout'
import { LINE_COLUMNS, Row } from './InvoiceParts'
import { DialogFooter, invoiceStatus } from './shared'

/** What is open over the record.
 *
 *  `credit` collects the reason before anything is sent — a step-up prompt
 *  that also asked for a reason would teach people to type anything into it
 *  to get to the code box; the re-verification, when the service wants one,
 *  is `useStepUp`'s. `discard` is the confirm before a draft is deleted. */
type Pending = 'credit' | 'discard' | null

/** The message a refusal carries, and whether it is the firm's state being
 *  unknown — which is drawn with the link that fixes it. */
function refusal(err: unknown, fallback: string): { text: string; settings: boolean } {
  if (isFirmStateUnknown(err)) return { text: (err as ApiError).message, settings: true }
  return { text: err instanceof ApiError ? err.message : fallback, settings: false }
}

const BACK = { to: '/billing', label: 'All invoices' }

export function InvoiceDetail() {
  const { invoiceId = '' } = useParams()
  const navigate = useNavigate()
  const phone = useIsPhone()
  const [invoice, setInvoice] = useState<Detail | null>(null)
  const [vocab, setVocab] = useState<BillingVocabulary | null>(null)
  const [clientName, setClientName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [checkout, setCheckout] = useState<PaymentSession | null>(null)
  /** The firm's state is unknown — ADR-0056 §2 — drawn with its link. */
  const [stateRefused, setStateRefused] = useState<string | null>(null)

  const fail = useCallback((err: unknown, fallback: string) => {
    const { text, settings } = refusal(err, fallback)
    if (settings) setStateRefused(text)
    else setError(text)
  }, [])
  const issueFailed = useCallback(
    (err: unknown) => fail(err, 'The invoice could not be issued.'),
    [fail],
  )
  const creditFailed = useCallback(
    (err: unknown) => {
      setPending(null)
      fail(err, 'The credit note could not be issued.')
    },
    [fail],
  )
  const issueStepUp = useStepUp(issueFailed)
  const creditStepUp = useStepUp(creditFailed)

  const load = useCallback(async () => {
    try {
      const [detail, v] = await Promise.all([billing.invoice(invoiceId), billing.vocabulary()])
      setInvoice(detail.data)
      setVocab(v.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That invoice could not be loaded.')
    }
  }, [invoiceId])

  useEffect(() => {
    void load()
  }, [load])

  // The client's name for the header. An issued invoice carries it frozen in
  // its snapshot; a draft does not yet, so its name is read once. A failure
  // only costs the fact — the invoice is still the invoice.
  const snapshotName = invoice?.client_snapshot?.name || null
  const clientId = invoice?.client_id
  useEffect(() => {
    if (!clientId || snapshotName) return
    let cancelled = false
    void clients
      .get(clientId)
      .then((r) => !cancelled && setClientName(r.data.legal_name))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [clientId, snapshotName])

  // The two gated actions. Each throws on a refusal — `useStepUp` reads
  // `STEPUP_REQUIRED` off the first try and hands everything else to `fail`.
  const issue = async (assertion?: string) => {
    const resp = await billing.issue(invoiceId, assertion)
    setNotice(`Issued as ${resp.data.number}.`)
    await load()
  }

  const credit = async (assertion?: string) => {
    const resp = await billing.creditNote(invoiceId, assertion, { reason })
    setNotice(
      `Credit note ${resp.data.credit_note.number} issued${
        resp.data.invoice.status === 'cancelled' ? ' — this invoice is cancelled.' : '.'
      }`,
    )
    setPending(null)
    setReason('')
    await load()
  }

  const startGated = async (stepUp: typeof issueStepUp, action: (assertion?: string) => Promise<void>) => {
    setBusy(true)
    setError(null)
    setStateRefused(null)
    try {
      await stepUp.attempt(action)
    } finally {
      setBusy(false)
    }
  }

  /**
   * A draft's tax, changed — C27, ADR-0056 §3. Saved as it is changed (the
   * pattern of the service record's switches), and the screen then shows the
   * server's split rather than one worked out here. *The firm's own state* is
   * sent as that state, or as blank (which the service reads as the same)
   * when the firm has not said which state it is in.
   */
  const saveTax = async (patch: { place_of_supply?: string; reverse_charge?: boolean }) => {
    setBusy(true)
    setError(null)
    setStateRefused(null)
    try {
      await billing.updateInvoice(invoiceId, patch)
      await load()
    } catch (err) {
      fail(err, 'That change could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Open a rendered document.
   *
   * The object URL is revoked a minute later rather than immediately: revoking
   * it at once races the new tab's own fetch and produces a blank viewer, and
   * never revoking it pins every invoice a firm clicked through for the life of
   * the tab.
   */
  const openPdf = async (path: string) => {
    setError(null)
    try {
      const url = await pdfUrl(path)
      window.open(url, '_blank', 'noopener')
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That document could not be rendered.')
    }
  }

  /**
   * Take the fee online — FR-G7, ADR-0027 §2 (w11a).
   *
   * The button only exists when the server has already said the firm's gateway
   * is active, so this is not the gate; it is the request. The answer is still
   * checked, because the integration can go inactive between the vocabulary
   * read and the click, and a refusal says which kind of no.
   *
   * A Razorpay order opens Checkout **in this page** (the client pays at the
   * desk, or on the partner's screen); a hosted checkout navigates. When
   * Checkout says it succeeded, **nothing is written**: the receipt arrives
   * with `integration.payment.captured`, so the screen says so and re-reads a
   * few seconds later rather than claiming the invoice is paid.
   */
  const startPayment = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const resp = await billing.paymentSession(invoiceId)
      setCheckout(resp.data)
      await openCheckout(resp.data, {
        onPaid: () => {
          setNotice(PAID_NOTICE)
          window.setTimeout(() => void load(), 5_000)
        },
      })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'The payment window could not be opened. Check the connection and try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  const discard = async () => {
    setBusy(true)
    try {
      await billing.deleteInvoice(invoiceId)
      navigate('/billing')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That draft could not be discarded.')
      setPending(null)
    } finally {
      setBusy(false)
    }
  }

  const lineColumns = useMemo(
    () => (phone ? LINE_COLUMNS.filter((c) => c.phone).map((c) => ({ ...c, width: 0 })) : LINE_COLUMNS),
    [phone],
  )

  if (!invoice) {
    return <RecordPage back={BACK} title="Invoice" loading error={error} />
  }

  const blocked = issueBlock(invoice, vocab)
  const status = invoiceStatus(invoice, iso(new Date()))
  const firmState = vocab?.firm_supply_state ?? null
  const isDraft = invoice.status === 'draft'
  const canEditTax = isDraft && Boolean(vocab?.can_update)
  const rcmNote = reverseChargeNote(invoice)
  const owing = invoice.status === 'issued' && invoice.outstanding !== '0.00'
  const payOnline = owing && Boolean(vocab?.gateway_active)
  const pdfPath = `/api/v1/billing/invoices/${invoice.id}/pdf`

  const facts: RecordFact[] = [
    { label: 'Client', value: snapshotName ?? clientName ?? '—' },
    { label: 'Issued', value: invoice.issue_date ?? 'Not yet' },
    ...(invoice.due_date ? [{ label: 'Due', value: invoice.due_date }] : []),
    { label: invoice.reverse_charge ? 'Amount due' : 'Total', value: rupees(invoice.grand_total) },
    ...(invoice.reverse_charge ? [{ label: 'Reverse charge', value: 'Yes' }] : []),
    ...(invoice.status === 'issued'
      ? [{ label: 'Balance', value: rupees(invoice.outstanding) }]
      : []),
    ...(invoice.origin === 'action' ? [{ label: 'Raised by', value: 'the case engine' }] : []),
  ]

  const pdfButton = (
    <Button onClick={() => void openPdf(pdfPath)}>
      <Download size={16} aria-hidden /> Open the PDF
    </Button>
  )

  let primary = pdfButton
  if (invoice.status === 'draft') {
    primary = (
      <span title={blocked ?? undefined}>
        <Button
          onClick={() => void startGated(issueStepUp, issue)}
          disabled={Boolean(blocked) || busy}
          loading={busy && !issueStepUp.challenging}
        >
          Issue
        </Button>
      </span>
    )
  } else if (payOnline) {
    primary = (
      <Button onClick={() => void startPayment()} disabled={busy}>
        <CreditCard size={16} aria-hidden /> Take payment online
      </Button>
    )
  }

  const more: DropdownMenuItem[] = []
  if (invoice.status === 'draft' || payOnline) {
    more.push({
      id: 'pdf',
      label: 'Open the PDF',
      icon: <Download size={14} aria-hidden />,
      onSelect: () => void openPdf(pdfPath),
    })
  }
  if (invoice.status === 'issued' && invoice.can_credit) {
    more.push({
      id: 'credit',
      label: 'Credit note',
      description: 'Cannot be undone.',
      icon: <FileMinus size={14} aria-hidden />,
      tone: 'danger',
      separatorBefore: more.length > 0,
      disabled: busy,
      onSelect: () => setPending('credit'),
    })
  }
  if (invoice.status === 'draft') {
    more.push({
      id: 'discard',
      label: 'Discard draft',
      icon: <Trash2 size={14} aria-hidden />,
      tone: 'danger',
      separatorBefore: more.length > 0,
      disabled: busy,
      onSelect: () => setPending('discard'),
    })
  }

  return (
    <RecordPage
      back={BACK}
      title={invoice.number ?? 'Draft invoice'}
      status={{ kind: 'invoice', status }}
      facts={facts}
      primary={primary}
      more={more}
      error={error}
    >
      {notice && <Alert tone="success">{notice}</Alert>}
      {stateRefused && (
        <Alert tone="error">
          <span data-firm-state-refusal>
            {stateRefused}{' '}
            <Link to={FIRM_STATE_HREF} className="font-medium underline">
              Open Settings → Your firm
            </Link>
          </span>
        </Alert>
      )}
      {blocked && <Alert tone="warning">{blocked}</Alert>}
      {invoice.status === 'cancelled' && (
        <Alert tone="info">
          This invoice was cancelled in full by a credit note. It keeps its number — a firm’s
          numbering has no gaps — and it is no longer owed.
        </Alert>
      )}

      {/* Side by side only from `2xl`: with the rail, a 1280 screen left the
          lines two thirds of ~990px and Rate, GST and Amount scrolled away. */}
      <div className="grid gap-6 2xl:grid-cols-3">
        <div className="min-w-0 2xl:col-span-2">
          <RecordSection
            id="lines"
            icon={<ListOrdered size={18} aria-hidden />}
            title="Lines"
            description={`${invoice.lines.length} ${invoice.lines.length === 1 ? 'line' : 'lines'}, before tax.`}
          >
            <DataTable
              columns={lineColumns}
              rows={invoice.lines}
              rowKey={(line) => line.id}
              empty={<span className="text-muted-foreground text-sm">No lines yet.</span>}
              className="bg-card"
            />
          </RecordSection>
        </div>

        <RecordSection id="total" icon={<Receipt size={18} aria-hidden />} title="Total">
          <dl className="space-y-1 text-sm">
            <Row label="Taxable value" value={rupees(invoice.subtotal)} />
            {taxRows(invoice).map((row) => (
              <Row
                key={row.label}
                label={invoice.reverse_charge ? `${row.label} (payable by the client)` : row.label}
                value={rupees(row.amount)}
              />
            ))}
            {invoice.round_off !== '0.00' && (
              <Row label="Rounding" value={rupees(invoice.round_off)} />
            )}
            <div className="border-border mt-2 border-t pt-2">
              {/* The server's `grand_total`: on reverse charge it is already the
                  taxable value — the screen subtracts nothing (ADR-0056 §4). */}
              <Row
                label={invoice.reverse_charge ? 'Amount due' : 'Total'}
                value={rupees(invoice.grand_total)}
                strong
              />
            </div>
            {invoice.status === 'issued' && (
              <>
                <Row label="Received" value={rupees(invoice.amount_paid)} />
                <Row label="Outstanding" value={rupees(invoice.outstanding)} strong />
              </>
            )}
          </dl>
          {rcmNote && (
            <p className="text-foreground mt-3 text-xs" data-reverse-charge>
              {rcmNote}
            </p>
          )}
          <p className="text-muted-foreground mt-3 text-xs">
            {invoice.is_interstate
              ? 'Interstate supply — IGST at the whole rate.'
              : 'Intrastate supply — CGST and SGST at half each.'}
            {invoice.place_of_supply
              ? ` Place of supply: ${invoice.place_of_supply}.`
              : ' No place of supply on this invoice — set your firm’s default in Settings.'}
          </p>

          {canEditTax && (
            <div className="border-border mt-4 space-y-3 border-t pt-3" data-draft-tax>
              <Select
                id="draft-place"
                label="Place of supply"
                value={invoice.place_of_supply ?? OWN_STATE}
                disabled={busy}
                onChange={(e) =>
                  void saveTax({
                    place_of_supply: e.target.value === OWN_STATE ? (firmState ?? '') : e.target.value,
                  })
                }
                hint="Until it is issued. The split above follows it."
              >
                {placeOfSupplyOptions(firmState).map((option) => (
                  <option key={option.value || 'own'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Switch
                checked={Boolean(invoice.reverse_charge)}
                disabled={busy}
                label="Tax payable on reverse charge"
                onChange={(e) => void saveTax({ reverse_charge: e.target.checked })}
              />
            </div>
          )}

          {owing && !vocab?.gateway_active && (
            <p className="border-border text-muted-foreground mt-4 border-t pt-3 text-xs">
              {gatewayReason(vocab?.gateway_reason ?? null)} Receipts are recorded by hand —
              every mode records the same way.
            </p>
          )}
          {owing && checkout && !checkout.available && (
            <p className="text-muted-foreground mt-2 text-xs">
              {gatewayRefusal(checkout.reason, checkout.detail)}
            </p>
          )}
        </RecordSection>
      </div>

      {invoice.credit_notes.length > 0 && (
        <RecordSection
          id="credit-notes"
          icon={<FileMinus size={18} aria-hidden />}
          title="Credit notes"
          description="Each opens as its own PDF."
        >
          <ul className="divide-border divide-y text-sm">
            {invoice.credit_notes.map((note) => (
              <li
                key={note.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 first:pt-0 last:pb-0"
              >
                <Button
                  variant="link"
                  onClick={() => void openPdf(`/api/v1/billing/credit-notes/${note.id}/pdf`)}
                >
                  {note.number}
                </Button>
                <span className="text-muted-foreground">
                  {note.scope === 'full' ? 'full' : 'partial'} · {rupees(note.grand_total)} ·{' '}
                  {note.issue_date}
                </span>
                <span className="min-w-0 break-words">{note.reason}</span>
              </li>
            ))}
          </ul>
        </RecordSection>
      )}

      <StepUpDialog
        {...issueStepUp.dialog}
        title="Issue this invoice"
        what="Issuing a tax invoice cannot be undone. It takes the next number in your firm’s series, freezes your details and the client’s onto the document, and posts it to the client’s ledger. A mistake after this point is corrected by a credit note."
      />

      {/* The kit's dialog, bounded by the window with a body that scrolls. It
          steps aside while the step-up asks, and comes back if that is
          closed — two kit dialogs at once each read a click in the other as
          an outside click. */}
      <Dialog
        open={pending === 'credit' && !creditStepUp.challenging}
        onClose={() => setPending(null)}
        title="Credit this invoice"
      >
        <p className="text-muted-foreground mb-4 text-sm">
          A credit note covering every line in full cancels the invoice. Give the reason — it is
          printed on the note and it is the first thing anybody asks about one.
        </p>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
        />
        <DialogFooter onCancel={() => setPending(null)}>
          <span title={reason.trim() ? undefined : 'Give the reason first.'}>
            <Button
              onClick={() => void startGated(creditStepUp, credit)}
              disabled={!reason.trim() || busy}
              loading={busy && !creditStepUp.challenging}
            >
              Continue
            </Button>
          </span>
        </DialogFooter>
      </Dialog>

      <StepUpDialog
        {...creditStepUp.dialog}
        title="Issue a credit note"
        what={`This reverses the invoice and posts the credit to the client’s ledger. It cannot be undone, and the invoice keeps its number.`}
      />

      <ConfirmDialog
        open={pending === 'discard'}
        onClose={() => setPending(null)}
        title="Discard this draft?"
        confirmLabel="Discard draft"
        tone="danger"
        busy={busy}
        onConfirm={() => void discard()}
      >
        <p className="text-muted-foreground text-sm">
          The draft and its lines are deleted. It never took a number, so your series is
          unaffected. Any pass-through expenses on it become billable again.
        </p>
      </ConfirmDialog>
    </RecordPage>
  )
}
